import { Auth0Provider } from '@auth0/auth0-react';
import { ChakraProvider, extendTheme } from '@chakra-ui/react';
import React from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

import { url as commonUrl } from '@finley-technologies/common';

import generatedIntrospection from 'types/dashboard-core-Introspection.generated.d';

import { AnalyticsProvider } from '~/contexts/AnalyticsProvider/AnalyticsProvider';
import { ApolloClientProvider } from '~/contexts/ApolloClientProvider';
import {
  useFeatureFlags,
  FeatureFlagProvider,
} from '~/contexts/FeatureFlagProvider';
import { ReactQueryProvider } from '~/contexts/ReactQueryProvider';
import { UserAuthProvider } from '~/contexts/UserAuthProvider';
import { LayoutContextProvider } from '~/packages/common-frontend/layout/LayoutProvider';
import { theme } from '~/packages/common-frontend/theme';

type InnerProvidersProps = { children: React.ReactNode };
export const InnerProviders = ({ children }: InnerProvidersProps) => {
  const flags = useFeatureFlags();
  return (
    <ApolloClientProvider
      possibleTypes={generatedIntrospection.possibleTypes}
      uri={commonUrl.getGraphqlapiUrl()}
      headers={{
        // Execution mode is set to "live" by default, and overridden to "sandbox" via the
        // <SandboxModeProvider /> component on the relevant routes
        'x-finley-execution-mode': 'live',
        // Used for ValleyBank
        'x-target': flags.targetHeaderValue,
      }}
    >
      {children}
    </ApolloClientProvider>
  );
};

type ProvidersProps = { children: React.ReactNode };
export const Providers = ({ children }: ProvidersProps) => {
  return (
    <AnalyticsProvider clientName="dashboard">
      <ChakraProvider theme={extendTheme(theme)}>
        <FeatureFlagProvider>
          <ReactQueryProvider>
            <UserAuthProvider enableCrossTabLogout>
              <Auth0Provider
                domain={commonUrl.getAuth0Domain()}
                clientId={commonUrl.getAuth0ClientId()}
                authorizationParams={{
                  // eslint-disable-next-line camelcase
                  redirect_uri: commonUrl.getAuth0CallbackUrl(),
                  audience: `${commonUrl.getAuth0ApiUrl()}/`,
                }}
              >
                <LayoutContextProvider>
                  <DndProvider backend={HTML5Backend}>
                    <InnerProviders>{children}</InnerProviders>
                  </DndProvider>
                </LayoutContextProvider>
              </Auth0Provider>
            </UserAuthProvider>
          </ReactQueryProvider>
        </FeatureFlagProvider>
      </ChakraProvider>
    </AnalyticsProvider>
  );
};
